/**
 * accountDetailsAdmin.service.ts — Admin > Account > Details.
 *
 * The corporate dashboard's Edit Account fields for the signed-in account,
 * through the same API v5 calls corp makes (components/accounts-table.tsx):
 *   - GET / PUT /account-details: the aa_accounts row, plus its country and
 *     currency (accounts_country / accounts_currency, end-dated on change);
 *   - PUT /app-account-settings: the General Intake Form;
 *   - GET /currency-codes for the Currency picker. Countries, a country's
 *     timezones and the e-forms come from their existing services.
 * Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AccountDetails {
  accountId: number;
  name: string;
  phone: string;
  contactEmail: string;
  /** "Admin Email". */
  accountEmail: string;
  /** A tzoneID, 0 for none. */
  timezone: number;
  countryID: number;
  currencyID: number;
  generalIntakeFormID: number;
  /** 1 user's full name, 2 practice name, 3 other, 4 user & practice name. */
  userEmailsFrom: string;
  userEmailsFromOther: string;
  /** 1 FollowThatPatient, 2 practice name, 3 other. */
  systemEmailsFrom: string;
  systemEmailsFromOther: string;
  /** Auto-log out time in seconds (0 = the default window). */
  timeout: number;
  fontSizeIncrease: number;
  /** An S3 URL, or a legacy file name under /uimages/img{accountID}/. */
  logo: string;
  // Read-only.
  loginName: string;
  domainEmailLead: string;
  domainEmailReply: string;
  smsReplyNumber: string;
  smsConfirmNumber: string;
}

/** What PUT /account-details takes. A key left out is left as it is. */
export type AccountDetailsPayload = Partial<{
  name: string;
  phone: string;
  contactEmail: string;
  accountEmail: string;
  timezone: number;
  countryID: number;
  currencyID: number;
  userEmailsFrom: string;
  userEmailsFromOther: string;
  systemEmailsFrom: string;
  systemEmailsFromOther: string;
  timeout: number;
  fontSizeIncrease: number;
  logo: string;
}>;

export interface CurrencyChoice {
  id: number;
  label: string;
}

const str = (v: unknown) => (v == null ? '' : String(v));
const num = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function getAccountDetails(): Promise<AccountDetails> {
  return apiClient.get<ApiResponse<Record<string, unknown>>>('/account-details').then((r) => {
    const d = r.data.data ?? {};
    return {
      accountId: num(d.accountID),
      name: str(d.name),
      phone: str(d.phone),
      contactEmail: str(d.contactEmail),
      accountEmail: str(d.accountEmail),
      timezone: num(d.timezone),
      countryID: num(d.countryID),
      currencyID: num(d.currencyID),
      generalIntakeFormID: num(d.generalIntakeFormID),
      userEmailsFrom: str(d.userEmailsFrom) || '1',
      userEmailsFromOther: str(d.userEmailsFromOther),
      systemEmailsFrom: str(d.systemEmailsFrom) || '1',
      systemEmailsFromOther: str(d.systemEmailsFromOther),
      timeout: num(d.timeout),
      fontSizeIncrease: num(d.fontSizeIncrease),
      logo: str(d.logo).trim(),
      loginName: str(d.loginName),
      domainEmailLead: str(d.domainEmailLead),
      domainEmailReply: str(d.domainEmailReply),
      smsReplyNumber: str(d.smsReplyNumber),
      smsConfirmNumber: str(d.smsConfirmNumber),
    };
  });
}

export function updateAccountDetails(payload: AccountDetailsPayload): Promise<void> {
  return apiClient.put('/account-details', payload).then(() => undefined);
}

export function updateGeneralIntakeForm(generalIntakeFormID: number): Promise<void> {
  return apiClient.put('/app-account-settings', { generalIntakeFormID }).then(() => undefined);
}

export function listCurrencies(): Promise<CurrencyChoice[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/currency-codes').then((r) =>
    (Array.isArray(r.data?.data) ? r.data.data : [])
      .map((c) => {
        const abbr = str(c.currencyAbbr).trim();
        const name = str(c.currencyName).trim();
        return { id: num(c.currencyID), label: abbr ? `${name} (${abbr})` : name };
      })
      .filter((c) => c.id > 0),
  );
}
